import { Container, Text } from "@react-three/uikit";
import { useEffect, useState } from "react";

export interface ChannelOverlayProps {
  channel: number;
  channelName?: string;
  visible: boolean;
  onHide?: () => void;
  autoHideDelay?: number; // ms before auto-hiding (default: 3000)
  /** If true, the overlay stays visible and never auto-hides */
  persistent?: boolean;
}

/**
 * Retro CRT-style channel overlay
 * Displays channel number in corner when switching
 */
export function ChannelOverlay({
  channel,
  channelName,
  visible,
  onHide,
  autoHideDelay = 3000,
  persistent = false,
}: ChannelOverlayProps) {
  const [opacity, setOpacity] = useState(0);

  // Fade in/out animation (persistent channels stay visible)
  useEffect(() => {
    if (visible || persistent) {
      setOpacity(1);
    } else {
      setOpacity(0);
    }
  }, [visible, persistent]);

  // Auto-hide after delay (skip if persistent)
  useEffect(() => {
    if (persistent) return; // Never auto-hide persistent overlays
    if (visible && onHide) {
      const timer = setTimeout(() => {
        onHide();
      }, autoHideDelay);
      return () => clearTimeout(timer);
    }
  }, [visible, channel, onHide, autoHideDelay, persistent]);

  if (!visible && !persistent && opacity === 0) {
    return null;
  }

  return (
    <Container
      positionType="absolute"
      positionTop={40}
      positionLeft={40}
      backgroundColor="rgba(0, 0, 0, 0.7)"
      paddingX={16}
      paddingY={8}
      gap={50}
      opacity={opacity}
    >
      <Text
        fontSize={48}
        fontWeight="bold"
        color="#00E500"
        fontFamily="monospace"
      >
        {channel.toString()}
      </Text>
      {channelName && (
        <Text
          fontSize={24}
          fontWeight="bold"
          color="#00E500"
          fontFamily="monospace"
        >
          {channelName}
        </Text>
      )}
    </Container>
  );
}
